function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
var _submenuMutationObserver = _classPrivateFieldLooseKey("submenuMutationObserver");
var _resizeListener = _classPrivateFieldLooseKey("resizeListener");
var _overflowSubmenuListener = _classPrivateFieldLooseKey("overflowSubmenuListener");
var _state = _classPrivateFieldLooseKey("state");
var _deactivateTimeout = _classPrivateFieldLooseKey("deactivateTimeout");
var _clearDeactivateTimeout = _classPrivateFieldLooseKey("clearDeactivateTimeout");
var _scheduleDeactivate = _classPrivateFieldLooseKey("scheduleDeactivate");
var _deactivate = _classPrivateFieldLooseKey("deactivate");
var _getOverflowListLinksHeight = _classPrivateFieldLooseKey("getOverflowListLinksHeight");
var _setFullOpenHeaderHeight = _classPrivateFieldLooseKey("setFullOpenHeaderHeight");
var _preloadImages = _classPrivateFieldLooseKey("preloadImages");
var _cleanupMutationObserver = _classPrivateFieldLooseKey("cleanupMutationObserver");
class HeaderMenu extends Component {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _cleanupMutationObserver, {
      value: _cleanupMutationObserver2
    });
    /**
     * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
     * @param {number} submenuHeight
     */
    Object.defineProperty(this, _setFullOpenHeaderHeight, {
      value: _setFullOpenHeaderHeight2
    });
    Object.defineProperty(this, _getOverflowListLinksHeight, {
      value: _getOverflowListLinksHeight2
    });
    /**
     * Schedule deactivation after a short delay (improves UX when moving to submenu)
     */
    Object.defineProperty(this, _scheduleDeactivate, {
      value: _scheduleDeactivate2
    });
    /**
     * Clear any pending deactivate timeout
     */
    Object.defineProperty(this, _clearDeactivateTimeout, {
      value: _clearDeactivateTimeout2
    });
    this.requiredRefs = ['overflowMenu'];
    /**
     * @type {MutationObserver | null}
     */
    Object.defineProperty(this, _submenuMutationObserver, {
      writable: true,
      value: null
    });
    /**
     * Debounced resize event listener to recalculate menu style
     */
    Object.defineProperty(this, _resizeListener, {
      writable: true,
      value: debounce(() => {
        setHeaderMenuStyle();
      }, 150)
    });
    Object.defineProperty(this, _overflowSubmenuListener, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _scheduleDeactivate)[_scheduleDeactivate]();
      }
    });
    /**
     * @type {State}
     */
    Object.defineProperty(this, _state, {
      writable: true,
      value: {
        activeItem: null
      }
    });
    /**
     * Timeout ID for delayed deactivation (improves UX when moving to submenu)
     * @type {ReturnType<typeof setTimeout> | null}
     */
    Object.defineProperty(this, _deactivateTimeout, {
      writable: true,
      value: null
    });
    /**
     * Activate the selected menu item immediately
     * @param {PointerEvent | FocusEvent} event
     */
    this.activate = event => {
      _classPrivateFieldLooseBase(this, _clearDeactivateTimeout)[_clearDeactivateTimeout]();
      this.dispatchEvent(new MegaMenuHoverEvent());
      if (!(event.target instanceof Element) || !this.headerComponent) return;
      let item = findMenuItem(event.target);
      if (!item || item == _classPrivateFieldLooseBase(this, _state)[_state].activeItem) return;
      const isDefaultSlot = event.target.slot === '';
      this.dataset.overflowExpanded = (!isDefaultSlot).toString();
      const previouslyActiveItem = _classPrivateFieldLooseBase(this, _state)[_state].activeItem;
      if (previouslyActiveItem) {
        previouslyActiveItem.ariaExpanded = 'false';
      }
      _classPrivateFieldLooseBase(this, _state)[_state].activeItem = item;
      this.ariaExpanded = 'true';
      item.ariaExpanded = 'true';
      let submenu = findSubmenu(item);
      const hasSubmenu = Boolean(submenu);
      if (!hasSubmenu && !isDefaultSlot) {
        submenu = this.overflowMenu;
      }
      if (submenu) {
        // Mark submenu as active for content-visibility optimization
        submenu.dataset.active = '';

        // Cleanup any existing mutation observer from previous menu activations
        _classPrivateFieldLooseBase(this, _cleanupMutationObserver)[_cleanupMutationObserver]();

        // Monitor DOM mutations to catch deferred content injection (from section hydration)
        _classPrivateFieldLooseBase(this, _submenuMutationObserver)[_submenuMutationObserver] = new MutationObserver(() => {
          requestAnimationFrame(() => {
            // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                _classPrivateFieldLooseBase(this, _cleanupMutationObserver)[_cleanupMutationObserver]();
              }
            });
          });
        });
        _classPrivateFieldLooseBase(this, _submenuMutationObserver)[_submenuMutationObserver].observe(submenu, {
          childList: true,
          subtree: true
        });

        // Auto-disconnect after 500ms to prevent memory leaks
        setTimeout(() => {
          _classPrivateFieldLooseBase(this, _cleanupMutationObserver)[_cleanupMutationObserver]();
        }, 500);
      }
      let finalHeight = submenu?.offsetHeight || 0;

      // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
      if (!isDefaultSlot) {
        const overflowListHeight = _classPrivateFieldLooseBase(this, _getOverflowListLinksHeight)[_getOverflowListLinksHeight]();
        if (hasSubmenu) {
          /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
           * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
          const overflowHeight = this.overflowMenu?.offsetHeight || 0;
          finalHeight = Math.max(overflowHeight, overflowListHeight);
        } else {
          finalHeight = overflowListHeight;
        }
      }
      if (!submenu) {
        // If there is no content to open, don't try to open it
        finalHeight = 0;
      }
      this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
      _classPrivateFieldLooseBase(this, _setFullOpenHeaderHeight)[_setFullOpenHeaderHeight](finalHeight);
      this.style.setProperty('--submenu-opacity', '1');
    };
    /**
     * Deactivate the active item immediately
     * @param {HTMLElement | null} [item]
     */
    Object.defineProperty(this, _deactivate, {
      writable: true,
      value: (item = _classPrivateFieldLooseBase(this, _state)[_state].activeItem) => {
        if (!item || item != _classPrivateFieldLooseBase(this, _state)[_state].activeItem) return;
        const submenu = findSubmenu(item);
        // Don't deactivate if the overflow menu, overflow list, or submenu is still being hovered
        if (this.overflowListHovered || this.overflowMenu?.matches(':hover') || submenu?.matches(':hover')) return;
        this.headerComponent?.style.setProperty('--submenu-height', '0px');
        _classPrivateFieldLooseBase(this, _setFullOpenHeaderHeight)[_setFullOpenHeaderHeight](0);
        this.style.setProperty('--submenu-opacity', '0');
        this.dataset.overflowExpanded = 'false';
        _classPrivateFieldLooseBase(this, _state)[_state].activeItem = null;
        this.ariaExpanded = 'false';
        item.ariaExpanded = 'false';

        // Remove active state from submenu after animation completes
        if (submenu) {
          delete submenu.dataset.active;
        }
      }
    });
    /**
     * Preload images that are set to load lazily.
     */
    Object.defineProperty(this, _preloadImages, {
      writable: true,
      value: () => {
        const images = this.querySelectorAll('img[loading="lazy"]');
        images?.forEach(image => image.removeAttribute('loading'));
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(_classPrivateFieldLooseBase(this, _preloadImages)[_preloadImages]);
    window.addEventListener('resize', _classPrivateFieldLooseBase(this, _resizeListener)[_resizeListener]);
    this.overflowMenu?.addEventListener('pointerleave', _classPrivateFieldLooseBase(this, _overflowSubmenuListener)[_overflowSubmenuListener]);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', _classPrivateFieldLooseBase(this, _resizeListener)[_resizeListener]);
    this.overflowMenu?.removeEventListener('pointerleave', _classPrivateFieldLooseBase(this, _overflowSubmenuListener)[_overflowSubmenuListener]);
    _classPrivateFieldLooseBase(this, _clearDeactivateTimeout)[_clearDeactivateTimeout]();
    _classPrivateFieldLooseBase(this, _cleanupMutationObserver)[_cleanupMutationObserver]();
  }
  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }
  get headerComponent() {
    return /** @type {HTMLElement | null} */this.closest('header-component');
  }
  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    const menu = findSubmenu(_classPrivateFieldLooseBase(this, _state)[_state].activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    // Check for both pointer and focus events - user moving to submenu should not close it
    const isMovingToSubmenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu = event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // Delay before closing to allow mouse to reach submenu (prevents accidental close on diagonal movement)
    _classPrivateFieldLooseBase(this, _scheduleDeactivate)[_scheduleDeactivate]();
  }
}
function _clearDeactivateTimeout2() {
  if (_classPrivateFieldLooseBase(this, _deactivateTimeout)[_deactivateTimeout]) {
    clearTimeout(_classPrivateFieldLooseBase(this, _deactivateTimeout)[_deactivateTimeout]);
    _classPrivateFieldLooseBase(this, _deactivateTimeout)[_deactivateTimeout] = null;
  }
}
function _scheduleDeactivate2() {
  _classPrivateFieldLooseBase(this, _clearDeactivateTimeout)[_clearDeactivateTimeout]();
  _classPrivateFieldLooseBase(this, _deactivateTimeout)[_deactivateTimeout] = setTimeout(() => {
    _classPrivateFieldLooseBase(this, _deactivate)[_deactivate]();
    _classPrivateFieldLooseBase(this, _deactivateTimeout)[_deactivateTimeout] = null;
  }, 200);
}
function _getOverflowListLinksHeight2() {
  const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
  if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

  /**
   * @param {(submenu: HTMLElement) => void} cb
   */
  const mapSubmenus = cb => {
    slottedMenuLinks.forEach(link => {
      const submenu = /** @type {HTMLElement | null} */link.querySelector('[ref="submenu[]"]');
      if (submenu) {
        cb(submenu);
      }
    });
  };
  mapSubmenus(submenu => {
    submenu.style.setProperty('display', 'none');
  });
  const height = this.overflowMenu?.offsetHeight || 0;
  mapSubmenus(submenu => {
    submenu.style.removeProperty('display');
  });
  return height;
}
function _setFullOpenHeaderHeight2(submenuHeight) {
  if (!this.headerComponent) return;
  const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
  const headerVisibleHeight = isOverlapSituation && this.headerComponent.offsetHeight > 0 ? /** @type {HTMLElement | null} */this.headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0 : this.headerComponent.offsetHeight;
  const nothingToOpen = submenuHeight === 0;
  const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);
  this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
}
function _cleanupMutationObserver2() {
  _classPrivateFieldLooseBase(this, _submenuMutationObserver)[_submenuMutationObserver]?.disconnect();
  _classPrivateFieldLooseBase(this, _submenuMutationObserver)[_submenuMutationObserver] = null;
}
if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
